/**
 * Sack type form: code, name and the empty-sack weight typed in grams or kg. uc-core wants
 * `tareKg` as a decimal string with at most 3 decimals and 1–3 whole digits
 * (src/modules/gate/dto/reference.dto.ts CreateSackTypeDto / UpdateSackTypeDto). Conversion is exact
 * (integer grams), never through floats.
 */
import { gramsToKgInput, parseKgToGrams } from '@/lib/weight'
import { gramsToKgString } from '../api/mappers'

export type WeightUnit = 'g' | 'kg'

export interface SackTypeDraft {
  code: string
  name: string
  weight: string
  unit: WeightUnit
  active: boolean
}

export interface SackTypeProblems {
  code?: string
  name?: string
  weight?: string
}

const CODE_RE = /^[A-Z0-9-]{2,20}$/
/** tareKg ≤ 999.999 kg (\d{1,3}). */
export const MAX_SACK_G = 999_999

export const emptySackTypeDraft = (): SackTypeDraft => ({ code: '', name: '', weight: '', unit: 'g', active: true })

/** Codes are upper case letters, digits and dashes — normalise as typed. */
export const normaliseCode = (s: string) => s.toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '')

/** Typed weight → integer grams; null when it cannot be read in that unit. */
export function weightToGrams(text: string, unit: WeightUnit): number | null {
  const t = text.trim().replace(/,/g, '')
  if (!t) return null
  if (unit === 'g') return /^\d{1,7}$/.test(t) ? Number(t) : null
  const g = parseKgToGrams(t)
  return g === null || g < 0 ? null : g
}

/** Grams → the text shown in the weight box for that unit ("120" g or "0.120" kg). */
export function gramsToWeightText(g: number, unit: WeightUnit): string {
  return unit === 'g' ? String(g) : gramsToKgInput(g)
}

/** Switch the unit keeping the same weight when the current text reads. */
export function switchUnit(d: SackTypeDraft, unit: WeightUnit): SackTypeDraft {
  if (unit === d.unit) return d
  const g = weightToGrams(d.weight, d.unit)
  return { ...d, unit, weight: g === null ? d.weight : gramsToWeightText(g, unit) }
}

export function draftFromSackType(s: { code: string; name: string; tareG: number; active: boolean }): SackTypeDraft {
  return { code: s.code, name: s.name, weight: String(s.tareG), unit: 'g', active: s.active }
}

export function validateSackTypeDraft(d: SackTypeDraft, mode: 'create' | 'edit'): SackTypeProblems {
  const p: SackTypeProblems = {}
  if (mode === 'create') {
    if (!d.code) p.code = 'Enter a short code'
    else if (!CODE_RE.test(d.code)) p.code = '2–20 capital letters, digits or dashes'
  }
  const name = d.name.trim()
  if (!name) p.name = 'Enter a name'
  else if (name.length > 60) p.name = 'At most 60 characters'
  if (!d.weight.trim()) p.weight = 'Enter the empty sack weight'
  else {
    const g = weightToGrams(d.weight, d.unit)
    if (g === null) p.weight = d.unit === 'g' ? 'Whole grams, e.g. 120' : 'kg with at most 3 decimals, e.g. 0.120'
    else if (g === 0) p.weight = 'An empty sack weighs more than 0 g'
    else if (g > MAX_SACK_G) p.weight = 'Too heavy for a sack (at most 999.999 kg)'
  }
  return p
}

export const sackTypeOk = (p: SackTypeProblems) => !p.code && !p.name && !p.weight

/** POST body. Call only when valid. */
export function createSackTypeBody(d: SackTypeDraft) {
  return {
    code: d.code,
    name: d.name.trim(),
    tareKg: gramsToKgString(weightToGrams(d.weight, d.unit) ?? 0),
    active: d.active,
  }
}
/** PATCH body (code cannot change). Call only when valid. */
export function updateSackTypeBody(d: SackTypeDraft) {
  return { name: d.name.trim(), tareKg: gramsToKgString(weightToGrams(d.weight, d.unit) ?? 0) }
}
